import { useSyncExternalStore } from "react";
import {
  ecrireProgression,
  lireProgression,
  marquerVu,
  noterDernier,
  type Dernier,
  type Progression,
  type Stockage,
} from "./progression.js";

/**
 * ============================================================================
 *  guideProgress.ts — Le store de la progression d'un guide
 * ----------------------------------------------------------------------------
 *  Petit store externe (comme `etatNavigation`), lu par React avec
 *  `useSyncExternalStore`. Un store par guide, créé à la première demande et gardé
 *  pour la session : mariage et séminaire ont chacun le leur. Le `localStorage` est lu
 *  UNE fois, à la création ; chaque changement le réécrit.
 *
 *  `last` change souvent (toutes les quelques secondes, en lecture) : l'écriture dans le
 *  stockage est fusionnée dans le temps (`ECRITURE_MS`) — sauf à `vider()`, appelé quand
 *  la page se masque ou se ferme, qui écrit tout de suite. Les abonnés, eux, sont
 *  prévenus à chaque changement de `watched` seulement : l'avancement de `last` ne doit
 *  pas redessiner le menu.
 * ==========================================================================*/

const ECRITURE_MS = 1500;

export interface StoreProgression {
  lire(): Progression;
  abonner(rappel: () => void): () => void;
  /** Un chapitre vu : à l'écran de fin. */
  marquerVu(chapterId: string): void;
  /** Où reprendre : écriture différée. */
  noter(dernier: Dernier): void;
  /** Écrit sans attendre (page masquée, fermeture, pause). */
  vider(): void;
}

export function creerStoreProgression(guideId: string, stockage: Stockage | null): StoreProgression {
  let etat = lireProgression(stockage, guideId);
  const abonnes = new Set<() => void>();
  let attente: ReturnType<typeof setTimeout> | undefined;
  let sale = false;

  const ecrire = () => {
    if (attente !== undefined) clearTimeout(attente);
    attente = undefined;
    if (!sale) return;
    sale = false;
    ecrireProgression(stockage, guideId, etat);
  };

  return {
    lire: () => etat,
    abonner(rappel) {
      abonnes.add(rappel);
      return () => abonnes.delete(rappel);
    },
    marquerVu(chapterId) {
      const suivant = marquerVu(etat, chapterId);
      if (suivant === etat) return;
      etat = suivant;
      sale = true;
      ecrire();
      abonnes.forEach((rappel) => rappel());
    },
    noter(dernier) {
      const avant = etat.last;
      if (avant && avant.chapterId === dernier.chapterId && avant.sceneIndex === dernier.sceneIndex && avant.t === dernier.t) return;
      // Même référence de `watched` : les abonnés n'ont pas à être prévenus (voir l'en-tête).
      etat = noterDernier(etat, dernier);
      sale = true;
      if (attente === undefined) attente = setTimeout(ecrire, ECRITURE_MS);
    },
    vider: ecrire,
  };
}

/* --- un store par guide, pour la session ------------------------------------ */

const stores = new Map<string, StoreProgression>();

function stockageDuNavigateur(): Stockage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    // Accéder au stockage lève dans certains navigateurs (cookies bloqués).
    return null;
  }
}

export function storeProgression(guideId: string): StoreProgression {
  let store = stores.get(guideId);
  if (!store) {
    store = creerStoreProgression(guideId, stockageDuNavigateur());
    stores.set(guideId, store);
  }
  return store;
}

const PROGRESSION_VIDE: Progression = { watched: [], last: null };

/** La progression d'un guide, ou une progression vide sans identifiant (`?demo`). */
export function useProgression(guideId: string | undefined): Progression {
  const store = guideId ? storeProgression(guideId) : null;
  return useSyncExternalStore(
    (rappel) => (store ? store.abonner(rappel) : () => undefined),
    () => (store ? store.lire() : PROGRESSION_VIDE),
  );
}
